<template>
  <div v-if="hasAssignments" class="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
    <!-- Header -->
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <div class="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
          <i class="pi pi-book text-sm"></i>
        </div>
        <h3 class="font-bold text-gray-800 text-sm">
          My {{ labels.SUBJECT }}s &amp; {{ labels.CLASS }}
        </h3>
      </div>
      <span class="text-[10px] text-gray-400 font-medium px-2 py-1 bg-gray-50 rounded-full">
        {{ totalCount }} assigned
      </span>
    </div>

    <!-- Secondary: Subject → ClassroomSections -->
    <div v-if="mySubjects.length" class="space-y-2">
      <div
        v-for="(sg, i) in mySubjects.slice(0, 5)"
        :key="i"
        class="flex items-start gap-3 px-3 py-2.5 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div class="w-7 h-7 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0 text-xs font-bold">
          {{ (sg.subject?.name || '?')[0] }}
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-xs font-semibold text-gray-800 truncate">{{ sg.subject?.name || '—' }}</p>
          <div class="flex flex-wrap gap-1 mt-1">
            <span
              v-for="cs in sg.classroomSections"
              :key="cs.id"
              class="text-[10px] px-1.5 py-0.5 rounded bg-white border border-gray-200 text-gray-600"
            >
              {{ cs.classroom?.name ? `${cs.classroom.name} - ${cs.name}` : cs.name }}
            </span>
          </div>
        </div>
      </div>
      <p v-if="mySubjects.length > 5" class="text-[10px] text-gray-400 text-center pt-1">
        +{{ mySubjects.length - 5 }} more
      </p>
    </div>

    <!-- Tertiary: Courses -->
    <div v-if="myCourses.length" class="space-y-2">
      <div
        v-for="(lc, i) in myCourses.slice(0, 5)"
        :key="lc.id ?? i"
        class="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div class="w-7 h-7 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0 text-[10px] font-bold">
          {{ (lc.course?.course_code || '?').substring(0, 3) }}
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-xs font-semibold text-gray-800 truncate">
            {{ lc.course?.course_code }} — {{ lc.course?.course_title }}
          </p>
          <p v-if="lc.course?.unit" class="text-[10px] text-gray-400 mt-0.5">
            {{ lc.course.unit }} unit{{ lc.course.unit > 1 ? 's' : '' }}
          </p>
        </div>
      </div>
      <p v-if="myCourses.length > 5" class="text-[10px] text-gray-400 text-center pt-1">
        +{{ myCourses.length - 5 }} more
      </p>
    </div>

    <!-- Quick Actions -->
    <div class="flex gap-2 mt-4 pt-3 border-t border-gray-100">
      <router-link
        v-if="canUploadScores"
        :to="{ name: 'admin-results' }"
        class="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 font-medium"
      >
        <i class="pi pi-upload text-[10px]"></i>
        Enter Scores
      </router-link>
      <router-link
        v-if="canViewResults"
        :to="{ name: 'admin-results' }"
        class="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 font-medium"
      >
        <i class="pi pi-chart-bar text-[10px]"></i>
        View Results
      </router-link>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useStaffDashboard } from '@/composables/useStaffDashboard';

const {
  labels,
  hasAssignments,
  mySubjects,
  myCourses,
  canUploadScores,
  canViewResults,
} = useStaffDashboard();

const totalCount = computed(() => mySubjects.value.length + myCourses.value.length);
</script>
